import { computed, ref } from 'vue'
import { instanceRoot } from '@general/ui-kit'

import { modulesFromHealth } from './helpSource'

// ── Qué módulos corren DE VERDAD en esta instancia ───────────────────────────
//
// El armazón descubre los módulos del sistema de ficheros al compilar
// (`registry.js`), y durante mucho tiempo eso bastó: lo que se le entregaba a un
// cliente era un repositorio con sus módulos y nada más.
//
// Ya no es así. `docker-compose.demo.yml` lo dice con todas las letras: la
// imagen es la PLATAFORMA y el `client.yaml` es la INSTANCIA — la misma imagen
// corre el ERP de un cliente y la demo, y quién monta qué lo decide el
// despliegue, no el build. Con eso, «está en la carpeta» dejó de significar
// «está instalado»: la demo lleva veinticinco módulos dentro y monta doce.
//
// El menú no se enteró porque se salva por otro camino: sus entradas llevan
// `permission`, y un módulo que no arranca no publica sus permisos, así que sus
// entradas no le salen a nadie. La documentación no tiene permisos —leer cómo
// funciona algo no es hacerlo— y por eso necesita preguntar.
//
// Se pregunta a `/health`, que es donde el servidor ya publica la lista y lo que
// el RUNBOOK de cada módulo manda mirar (§10.1). No hay endpoint nuevo ni
// permiso nuevo: la respuesta ya existía y nadie desde aquí la usaba.

/**
 * Una sola lectura por sesión, compartida por quien la pida.
 *
 * La guarda es la PROMESA y no el resultado: dos pantallas que preguntan a la
 * vez —el índice y un tema abierto de un enlace— tienen que hacer una petición,
 * no dos.
 */
let asked = null

/**
 * Los nombres de los módulos montados, o `null` si no se ha podido saber.
 *
 * `null` y no un conjunto vacío a propósito: son dos respuestas distintas y
 * quien llama hace cosas distintas con ellas. Vacío es «esta instancia no monta
 * ningún módulo de negocio», que es una instancia legítima y se enseña como
 * tal; `null` es «no lo sé», y ante eso no se enseña una lista de módulos que
 * a lo mejor no están.
 *
 * No lanza nunca. Si el servidor no contesta, la sección de ayuda es la menor
 * de las averías de esa pantalla y no tiene por qué ser además un error rojo.
 */
export function mountedModules() {
  if (!asked) {
    asked = fetch(`${instanceRoot()}/health`)
      .then(response => (response.ok ? response.json() : null))
      .then(modulesFromHealth)
      .catch(() => null)
      .then(result => {
        // Un fallo no se guarda: la siguiente visita a la ayuda vuelve a
        // preguntar. Cachear un `null` dejaría la documentación vacía hasta que
        // alguien recargara la página, por un corte de red de un segundo.
        if (result === null) asked = null
        return result
      })
  }
  return asked
}

/**
 * Lo mismo para una pantalla: dos referencias en vez de una promesa.
 *
 * `asking` es lo que impide el parpadeo, y es la razón de que esto sea un
 * composable y no tres pantallas repitiendo el mismo `.then()`: enseñar la
 * lista entera y encogerla medio segundo después es exactamente lo que este
 * filtro existe para evitar — durante ese medio segundo se lee la
 * documentación de los módulos que no se han comprado.
 */
export function useMountedModules() {
  // `undefined` mientras se pregunta; `null` si no se pudo saber, y entonces se
  // enseña lo que hay, que es lo que se hacía antes de poder preguntar.
  const installed = ref(undefined)
  mountedModules().then(answer => { installed.value = answer })

  return { installed, asking: computed(() => installed.value === undefined) }
}

/** Olvidar lo leído. Para los tests y para cuando termina una sesión. */
export function forgetInstance() {
  asked = null
}
